import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { IGridItem, IActiveElement, TLayout, TCompactType, IPositionParams } from '../grid.types'
import gridLayoutUtils from '../gridLayout.utils'
import usePositionParams from './usePositionParams'
import useForceUpdate from '@/shared/hooks/useForceUpdate'

interface IProps {
  layout: TLayout
  positionParams: IPositionParams
  onLayoutChange?: (layout: TLayout) => void
  compactType: TCompactType
  containerRef: RefObject<HTMLDivElement>
}

const useGridLayout = (props: IProps) => {
  const forceUpdate = useForceUpdate()
  const layoutRef = useRef<TLayout>(props.layout)
  const [activeElement, setActiveElement] = useState<IActiveElement>()

  const onColsParamChanged = useCallback(
    (cols: number) => {
      const layoutCopy = [...layoutRef.current]
      const newLayout = gridLayoutUtils.colCompact(layoutCopy, props.compactType, cols)
      layoutRef.current = newLayout
    },
    [layoutRef, props.compactType]
  )

  const { positionParams } = usePositionParams(props.containerRef, props.positionParams, onColsParamChanged)

  const onDrag = useCallback(
    (x: number, y: number, key: IGridItem['key']) => {
      const layoutCopy = [...layoutRef.current]
      const movingItem = layoutCopy.find((i) => i.key === key)
      if (!movingItem) return
      let newLayout = gridLayoutUtils.moveElement({
        layout: layoutCopy,
        el: movingItem,
        cols: positionParams.cols,
        compactType: props.compactType,
        x,
        y,
        isUserAction: true,
      })
      newLayout = gridLayoutUtils.compact(newLayout, props.compactType, positionParams.cols)
      forceUpdate()
      layoutRef.current = newLayout
    },
    [layoutRef, positionParams, props.compactType]
  )

  const onResize = useCallback(
    (width: number, height: number, key: IGridItem['key']) => {
      const layoutCopy = [...layoutRef.current]
      const movingItem = layoutCopy.find((i) => i.key === key)
      if (!movingItem) return
      movingItem.height = height
      movingItem.width = width
      let newLayout = gridLayoutUtils.moveElement({
        layout: layoutCopy,
        el: movingItem,
        cols: positionParams.cols,
        compactType: props.compactType,
        isUserAction: true,
      })
      newLayout = gridLayoutUtils.compact(newLayout, props.compactType, positionParams.cols)
      forceUpdate()
      layoutRef.current = newLayout
    },
    [layoutRef, positionParams, props.compactType]
  )

  const onActiveElementChange = useCallback(
    (activeElement: IActiveElement) => {
      if (!activeElement && typeof props.onLayoutChange === 'function') props.onLayoutChange(layoutRef.current)
      setActiveElement(activeElement)
    },
    [props.onLayoutChange, layoutRef]
  )

  useEffect(() => {
    layoutRef.current = gridLayoutUtils.colCompact(props.layout, props.compactType, positionParams.cols)
    forceUpdate()
  }, [props.layout])

  return { onDrag, onResize, onActiveElementChange, forceUpdate, activeElement, layoutRef, positionParams }
}

export default useGridLayout
